import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';
import { useMutation, useQueryClient } from 'react-query';
import produce from 'immer';

import { useBraze } from '@/components/Braze';
import { addSnackBar } from '@/components/SnackBar/redux';

import {
  WATCHLIST_QUERY_KEY,
  WATCHLISTS_QUERY_KEY,
} from '@/hooks/useWatchlist';
import type {
  WatchlistQueryClientData,
  WatchlistsQueryClientData,
} from '@/hooks/useWatchlist';

import { useSelectedWatchlist } from '../useSelectedWatchlist';
import useWatchlistQueryClient from '../useWatchlistQueryClient';

import {
  addQueryFn,
  createWatchlistQueryFn,
  deleteWatchlistQueryFn,
  removeQueryFn,
  renameWatchlistQueryFn,
} from './queryFn';

export default function useWatchlistMutations() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking();
  const { logCustomEvent } = useBraze();
  const watchlistQueryClient = useWatchlistQueryClient();
  const [selectedWatchlistId, selectWatchlist] = useSelectedWatchlist();

  const { mutate: addCompany, status: addCompanyStatus } = useMutation(
    addQueryFn,
    {
      onMutate: ({ includeSnackBar }) => {
        if (includeSnackBar) {
          dispatch(
            addSnackBar.trigger({
              isLoading: true,
              message: 'Adding company to Watchlist',
              lifeTime: 2000,
            })
          );
        }
      },
      onSuccess: (data, { watchlistId, includeSnackBar, autoAdded }) => {
        logCustomEvent('company_added', {
          add_to: 'watchlist',
          add_to_action: autoAdded ? 'auto' : 'manual',
          company_name: data.company.name,
          company_ticker: data.company.uniqueSymbol,
          company_market: data.company.exchangeCountryISO,
          company_industry_lvl_2: data.company.secondaryIndustry.name,
        });

        trackEvent({
          action: 'add_stock',
          name: data.company.name,
          unique_symbol: data.company.uniqueSymbol,
          market: data.company.exchangeCountryISO,
        });

        if (includeSnackBar) {
          const id = data.company.tickerSymbol;
          dispatch(
            addSnackBar.trigger({
              type: 'good',
              message: `${id} added to Watchlist`,
              lifeTime: 2000,
            })
          );
        }

        watchlistQueryClient.setQueriesData(watchlistId, (state) => {
          const alreadyExists = state.items.some(
            (item) => item.companyId === data.companyId
          );
          if (alreadyExists) {
            return state;
          }
          return produce(state, (draft) => {
            draft.items.unshift(data);
          });
        });

        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => {
            if (typeof state === 'undefined') {
              return state;
            }
            return produce(state, (draft) => {
              const updatedWatchlist = draft.find((w) => w.id === watchlistId);
              if (updatedWatchlist) {
                updatedWatchlist.items.unshift({ companyId: data.companyId });
                updatedWatchlist.totalItems++;
              }
            });
          }
        );
      },
      onSettled: (_, __, payload) => {
        queryClient.invalidateQueries(WATCHLISTS_QUERY_KEY);
        watchlistQueryClient.invalidateQueries(payload.watchlistId);
      },
      onError: (error: Error) => {
        let message = 'Failed to add company';
        if (error && error.message.includes('already in watchlist')) {
          message = 'This company is already in your watchlist';
        }
        dispatch(
          addSnackBar.trigger({
            type: 'bad',
            message,
            lifeTime: 2000,
          })
        );
      },
    }
  );

  const { mutate: removeCompany, status: removeCompanyStatus } = useMutation(
    removeQueryFn,
    {
      onMutate: ({ id, watchlistId }) => {
        /** Optimistically remove key */
        watchlistQueryClient.setQueriesData(watchlistId, (state) =>
          produce(state, (draft) => {
            draft.items = draft.items.filter((item) => item.companyId !== id);
          })
        );

        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => {
            if (typeof state === 'undefined') {
              return state;
            }
            return produce(state, (draft) => {
              const updatedWatchlist = draft.find((w) => w.id === watchlistId);
              const removedIndex =
                updatedWatchlist?.items.findIndex((i) => i.companyId === id) ??
                -1;
              if (updatedWatchlist && removedIndex !== -1) {
                updatedWatchlist.items.splice(removedIndex, 1);
                updatedWatchlist.totalItems--;
              }
            });
          }
        );
      },
      onSuccess: (deleted, { includeSnackBar, message }) => {
        if (deleted && includeSnackBar) {
          dispatch(
            addSnackBar.trigger({
              type: 'good',
              message: message || 'Removed from watchlist',
              lifeTime: 2000,
            })
          );
        }
      },
      onSettled: (_, __, payload) => {
        queryClient.invalidateQueries(WATCHLISTS_QUERY_KEY);
        watchlistQueryClient.invalidateQueries(payload.watchlistId);
      },
      onError: () => {
        dispatch(
          addSnackBar.trigger({
            type: 'bad',
            message: 'Failed to remove company',
            lifeTime: 1500,
          })
        );
      },
    }
  );
  const { mutate: createWatchlist, status: createWatchlistStatus } =
    useMutation(createWatchlistQueryFn, {
      onMutate: () => {
        dispatch(
          addSnackBar.trigger({
            isLoading: true,
            message: 'Creating list...',
            lifeTime: 2000,
          })
        );
      },
      onSuccess: (watchlist) => {
        trackEvent({
          action: 'success',
          modifier: 'createWatchlist',
        });
        dispatch(
          addSnackBar.trigger({
            type: 'good',
            message: 'New list created successfully',
            lifeTime: 2000,
          })
        );

        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => {
            if (typeof state === 'undefined') {
              return state;
            }
            return produce(state, (draft) => {
              draft.push(watchlist);
            });
          }
        );
        /**
         * Explicitly set query data for the watchlist query based on the new watchlist's id.
         * Prefer this to `watchlistQueryClient`, which doesn't handle non-existing query data.
         * Finally, select the new watchlist.
         */
        queryClient.setQueryData<WatchlistQueryClientData>(
          [WATCHLIST_QUERY_KEY, watchlist.id],
          watchlist
        );
        selectWatchlist(watchlist.id);
      },
      onSettled: () => {
        queryClient.invalidateQueries(WATCHLISTS_QUERY_KEY);
      },
      onError: () => {
        const message = 'Failed to create new watchlist';
        dispatch(
          addSnackBar.trigger({
            type: 'bad',
            message,
            lifeTime: 2000,
          })
        );
      },
    });

  const { mutate: renameWatchlist, status: renameWatchlistStatus } =
    useMutation(renameWatchlistQueryFn, {
      onMutate: ({ id: watchlistId, newName }) => {
        dispatch(
          addSnackBar.trigger({
            isLoading: true,
            message: 'Renaming list...',
            lifeTime: 2000,
          })
        );
        /** Optimistically rename watchlist */
        watchlistQueryClient.setQueriesData(watchlistId, (state) =>
          produce(state, (draft) => {
            draft.name = newName;
          })
        );
        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => {
            if (typeof state === 'undefined') {
              return state;
            }
            return produce(state, (draft) => {
              const renamedWatchlist = draft.find(
                ({ id }) => id === watchlistId
              );
              if (renamedWatchlist) {
                renamedWatchlist.name = newName;
              }
            });
          }
        );
      },
      onSuccess: (watchlist) => {
        const { name } = watchlist;
        trackEvent({
          action: 'success',
          modifier: 'renameWatchlist',
        });
        dispatch(
          addSnackBar.trigger({
            type: 'good',
            message: `List successfully renamed to ${name}`,
            lifeTime: 2000,
          })
        );
      },
      onSettled: (_, __, payload) => {
        watchlistQueryClient.invalidateQueries(payload.id);
        queryClient.invalidateQueries(WATCHLISTS_QUERY_KEY);
      },
      onError: () => {
        const message = 'Failed to rename watchlist';
        dispatch(
          addSnackBar.trigger({
            type: 'bad',
            message,
            lifeTime: 2000,
          })
        );
      },
    });

  const { mutate: deleteWatchlist, status: deleteWatchlistStatus } =
    useMutation(deleteWatchlistQueryFn, {
      onMutate: (payload) => {
        dispatch(
          addSnackBar.trigger({
            isLoading: true,
            message: 'Deleting list...',
            lifeTime: 1000,
          })
        );

        queryClient.setQueryData<WatchlistsQueryClientData>(
          WATCHLISTS_QUERY_KEY,
          (state) => state?.filter((w) => w.id !== payload.id)
        );
      },
      onSuccess: async (deleted, { id: deletedId }) => {
        if (deleted) {
          trackEvent({
            action: 'success',
            modifier: 'deleteWatchlist',
          });
          dispatch(
            addSnackBar.trigger({
              type: 'good',
              message: 'List successfully deleted',
              lifeTime: 2000,
            })
          );

          if (selectedWatchlistId === deletedId) {
            selectWatchlist(null);
          }
          /**
           * Remove query specifically for deletedId, then refetch queries matching
           * deletedId in case the `null` query was for the same id. Ensure refetch
           * completes before invalidating the watchlists query by awaiting it.
           */
          queryClient.removeQueries([WATCHLIST_QUERY_KEY, deletedId]);
          await watchlistQueryClient.refetchQueries(deletedId);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(WATCHLISTS_QUERY_KEY);
      },
      onError: () => {
        dispatch(
          addSnackBar.trigger({
            type: 'bad',
            message: 'Failed to delete list',
            lifeTime: 1500,
          })
        );
      },
    });

  return {
    addCompany,
    removeCompany,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    addCompanyStatus,
    createWatchlistStatus,
    removeCompanyStatus,
    renameWatchlistStatus,
    deleteWatchlistStatus,
  };
}
